import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { TelnyxRTC } from "@telnyx/webrtc";

import CancelButton from "../../components/CancelButton";
import CallResident from "../../common/icons/CallResident";
import PhoneCallActive from "../../common/icons/phone-call-yellow.svg";
import {
  decrementRecentCall,
  incrementRecentCall,
  pushNotification,
} from "../../redux/actions/app";
import {
  accessDenied,
  enqueueAccessRequest,
  openGate,
  persistLog,
} from "../../redux/actions/property";
import client from "../../common/client";
import { startRinging, stopRinging } from "../../common/lib/sounds";
import config from "../../common/config";
import {
  NORMALIZED_BUTTONS,
  NormalizedButtonEvent,
} from "../../common/normalized-hardware-models";
import firebase from "../../common/firebase";
import {
  Call as CallType,
  ClientCandidateMessage,
  ClientDenyMessage,
  ClientHangupMessage,
  createNotification,
  Device,
  Property,
  PropertyStaffUser,
  Resident,
  SIPCall,
  SIPMessage,
  TelnyxNotification,
} from "src/common/types";
import { History, Location, RootState, TypedDispatch } from "src";
import pusher from "src/common/lib/pusher";
import rawr from "src/common/lib/rawr";

type ComponentProps = {
  dispatch: TypedDispatch;
  recentCalls: number;
  resident: Resident;
  property: Property | null;
  device: Device;
  staff: PropertyStaffUser[];
  history: History;
  location: Location;
};

type ComponentState = {
  isConnected: boolean;
  call: CallType | null;
  peer: RTCPeerConnection | null;
  candidates: RTCIceCandidate[];
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  remoteVideo: MediaStreamTrack | null;
  remoteAudio: MediaStreamTrack | null;
};

const isActiveCallState = (callState: string) => {
  return !["hangup", "destroy", "purge"].includes(callState);
};

class Call extends Component<ComponentProps, ComponentState> {
  static propTypes = {
    history: PropTypes.any.isRequired,
  };

  localVideo: React.RefObject<any>;
  remoteVideoRef: React.RefObject<any>;
  remoteAudioRef: React.RefObject<any>;
  telnyxClient: any;
  activeSIPCall: SIPCall | null;
  mounted: boolean;
  hardTimeout: number;
  socket: WebSocket | null;

  constructor(props: ComponentProps) {
    super(props);

    this.localVideo = React.createRef();
    this.remoteVideoRef = React.createRef();
    this.remoteAudioRef = React.createRef();

    this.state = {
      isConnected: false,
      call: null,
      peer: null,
      candidates: [],
      localStream: null,
      remoteStream: null,
      remoteVideo: null,
      remoteAudio: null,
    };

    this.telnyxClient = null;
    this.activeSIPCall = null;
    this.mounted = false;
    this.hardTimeout = 0;
    this.socket = new WebSocket(
      `${config.webSocketUrl}/?clientId=${this.props.device.id}`
    );
  }

  async componentDidMount() {
    const { resident, dispatch, recentCalls, property } = this.props;

    this.mounted = true;
    this.socket = new WebSocket(
      `${config.webSocketUrl}/?clientId=${this.props.device.id}`
    );

    window.addEventListener("normalizedButtonEvent", this.handleButton);

    this.hardTimeout = window.setTimeout(() => {
      this.handleEscape();
    }, 300000);

    if (rawr) {
      console.debug("RPC: Call started, turning on fan(s)");
      rawr.methods.start_call();
    } else {
      console.warn("RPC: Trying to start call, but no client available");
    }

    this.socket.addEventListener("message", (event) => {
      const data = JSON.parse(event.data);

      switch (data.event) {
        case "newCall":
          this.handleNewCall();
          break;
        case "callAnswered":
          stopRinging();
          this.handleCallAnswered(data);
          break;

        case "endCall":
          this.handleEndCall(data);
          break;
        case "ICEcandidate":
          this.handleIceCandidate(data);
          break;
      }
    });

    if (property) {
      let channel = pusher.channel(
        `private-telephone-entry-property-${property.id}`
      );

      if (!channel) {
        channel = pusher.subscribe(
          `private-telephone-entry-property-${property.id}`
        );
      }
      channel.unbind("sip-message");
      channel.bind("sip-message", this.handleSIPMessage);
    }
    if (recentCalls > 10) {
      dispatch(
        pushNotification(
          createNotification(
            "ERROR",
            "You're doing that too much",
            null,
            "throttle"
          )
        )
      );

      this.handleEscape();
    } else {
      await this.createCall(resident);
    }
  }

  componentDidUpdate(prevProps: ComponentProps, prevState: ComponentState) {
    const { localStream, remoteStream, remoteAudio, remoteVideo } = this.state;

    if (
      localStream &&
      prevState.localStream !== localStream &&
      this.localVideo.current
    ) {
      this.localVideo.current.srcObject = localStream;
      this.localVideo.current.play();
    }

    if (remoteAudio !== null && remoteVideo !== null && !remoteStream) {
      let stream = new MediaStream();

      stream.addTrack(remoteVideo);
      stream.addTrack(remoteAudio);

      this.setState({ remoteStream: stream });
    }
    if (
      remoteStream &&
      prevState.remoteStream !== remoteStream &&
      this.remoteVideoRef.current &&
      !this.remoteVideoRef.current.srcObject
    ) {
      this.remoteVideoRef.current.srcObject = remoteStream;
      this.remoteVideoRef.current.play();
    }
  }

  componentWillUnmount() {
    this.mounted = false;

    if (rawr) {
      console.debug("RPC: Call ended, turning off fan(s)");
      rawr.methods.end_call();
    } else {
      console.warn("RPC: Trying to end call, but no client available");
    }

    window.removeEventListener("normalizedButtonEvent", this.handleButton);

    clearTimeout(this.hardTimeout);

    this.endCall();
  }

  createCall = async (resident: Resident) => {
    const { device, dispatch } = this.props;
    if (resident) {
      dispatch(
        persistLog(
          `RTC : Initiating call with resident ${resident.id} (user_enabled_video_calls=${resident.user_enabled_video_calls})`
        )
      );
    } else {
      dispatch(persistLog("RTC : Initiating call with property management"));
    }

    startRinging();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { exact: 240 } },
        audio: true,
      });

      if (!this.mounted) return;

      dispatch(persistLog("RTC : Generated stream", stream));

      this.setState({ localStream: stream });

      let track = stream.getVideoTracks()[0];
      let imageCapture = new ImageCapture(track);

      // Setting a 2 second timeout before calling grabFrame gives the camera
      // time to initialize and focus correctly.
      const CAMERA_INITIALIZATION_DELAY = 2000;
      await new Promise((resolve) =>
        setTimeout(resolve, CAMERA_INITIALIZATION_DELAY)
      );

      if (!this.mounted) return;

      const imageBitmap = await imageCapture.grabFrame();

      let imageData = null;
      dispatch(persistLog("RTC : Grabbed frame"));
      try {
        const canvas = document.createElement("canvas");
        canvas.width = imageBitmap.width;
        canvas.height = imageBitmap.height;
        canvas.getContext("2d")?.drawImage(imageBitmap, 0, 0);
        imageData = canvas.toDataURL();
      } catch (e) {
        dispatch(persistLog("RTC : Error rendering canvas:", e));
      }
      imageBitmap.close();

      if (!this.mounted) return;

      const callResponse = await client.post("telephone-entry/calls", {
        json: {
          tenantId: resident ? resident.id : null,
          imageData: imageData,
          isGen4: false,
        },
      });

      const json = await callResponse.json();

      let peer: RTCPeerConnection | null = null;

      dispatch(persistLog("RTC : Created new call", json));

      if (
        resident &&
        resident.user_enabled_video_calls &&
        device.enable_video_calls
      ) {
        firebase.logAnalyticsEvent("initiate_video_call");

        // TODO: Return these from the API
        const iceServers = [
          {
            url: "stun:stun.telnyx.com:3478",
            urls: "stun:stun.telnyx.com:3478",
          },
          {
            url: "turn:turn.telnyx.com:3478",
            urls: "turn:turn.telnyx.com:3478",
            username: "testuser",
            credential: "testpassword",
          },
        ];
        peer = new RTCPeerConnection({
          iceServers,
        });

        setTimeout(() => {
          const { call } = this.state;
          if (
            call &&
            call.intercomCallId === json.intercomCallId &&
            peer &&
            !["connected", "completed", "closed"].includes(
              peer.iceConnectionState
            )
          ) {
            dispatch(persistLog("RTC : Falling back to SIP call..."));

            this.fallbackToSIP(resident, json);
          }
        }, 20000);

        // addStream() and onaddstream is deprecated, typescript unhappy
        // @ts-ignore
        peer.ontrack = (track: MediaStreamTrackEvent) => {
          if (track.track.kind === "video") {
            this.setState({ remoteVideo: track.track });
          } else {
            this.setState({ remoteAudio: track.track });
          }
        };

        peer.oniceconnectionstatechange = () => {
          dispatch(
            persistLog("ice connection state changed", peer?.iceConnectionState)
          );
          if (
            peer?.iceConnectionState === "failed" ||
            peer?.iceConnectionState === "closed"
          ) {
            this.endCall(false);
            dispatch(
              pushNotification(
                createNotification(
                  "ERROR",
                  "Call disconnected",
                  "Please try again later",
                  "sorryNoAccess"
                )
              )
            );
          }
        };

        peer.onicecandidate = (e: RTCPeerConnectionIceEvent) => {
          dispatch(persistLog("sdp candidate generated: ", e));
          if (e.candidate) {
            const data = {
              remoteClient: this.props.resident.id,
              rtcMessage: e.candidate,
            };
            this.socket?.send(JSON.stringify({ event: "ICEcandidate", data }));
          }
        };

        // addStream() and onaddstream is deprecated, typescript unhappy
        // @ts-ignore
        peer.addStream(stream);
      } else {
        if (!this.mounted) return;
        await this.fallbackToSIP(resident, json);
      }

      dispatch(incrementRecentCall(resident));
      setTimeout(() => {
        dispatch(decrementRecentCall(resident));
      }, config.decrementCallRateLimitTimer);

      this.setState({
        call: json,
        peer,
      });
    } catch (e) {
      console.error(e);
      this.handleEscape();
    }
  };

  endCall = (shouldOpenGate?: boolean) => {
    const { call } = this.state;
    const { resident, staff, dispatch } = this.props;

    this.cleanup();
    dispatch(
      persistLog(
        `RTC : Ending call ${call ? call.intercomCallId : "ID unknown"}`
      )
    );

    let code;
    if (call && call.tenantId) {
      if (resident.property_access_credential) {
        code = resident.property_access_credential.code;
      } else {
        dispatch(
          persistLog(
            `RTC : Error looking up access code for tenant id=${call.tenantId}`
          )
        );
      }
    } else {
      // we don't know which manager triggered the gate, so find first available staff access code
      const managerWithCode = staff.find(
        (staff: PropertyStaffUser) => staff.property_credentials.length > 0
      );
      if (managerWithCode) {
        code = managerWithCode.property_credentials[0].code;
      } else {
        dispatch(persistLog("RTC : Error looking up access code for manager"));
      }
    }
    if (shouldOpenGate) {
      dispatch(openGate(code));
      dispatch(enqueueAccessRequest(code, true));
    } else if (shouldOpenGate === false) {
      dispatch(persistLog("RTC : Resident denied gate access"));
      dispatch(accessDenied(code));
    }

    this.setState(
      {
        isConnected: false,
        call: null,
        peer: null,
        candidates: [],
        localStream: null,
        remoteStream: null,
      },
      () => {
        this.handleEscape();
      }
    );

    this.telnyxClient = null;
  };

  cleanup = () => {
    const { peer, localStream } = this.state;
    const { dispatch } = this.props;

    stopRinging();

    this.socket?.close();

    if (peer) {
      // @ts-ignore TODO FIXME can't set this to undefined in typescript
      peer.oniceconnectionstatechange = undefined; // prevent this being called during hangup so we don't get an error notification
      peer.close();
    }

    if (localStream) {
      for (let track of localStream.getTracks()) {
        track.stop();
      }
    }

    if (this.activeSIPCall) {
      dispatch(persistLog("SIP : Call ended"));
      if (isActiveCallState(this.activeSIPCall.state)) {
        this.activeSIPCall.hangup();
      }
      this.activeSIPCall = null;
    }
    if (this.telnyxClient) {
      this.telnyxClient.off("telnyx.ready");
      this.telnyxClient.off("telnyx.notification");
      this.telnyxClient.off("telnyx.error");
      this.telnyxClient.disconnect();
    }
  };

  fallbackToSIP = async (resident: Resident, call: CallType) => {
    const { peer } = this.state;
    const { property, dispatch } = this.props;

    if (!property) {
      return;
    }

    dispatch(persistLog("SIP : Initializing a new SIP call..."));
    if (resident) {
      firebase.logAnalyticsEvent("initiate_fallback_call");
    } else {
      firebase.logAnalyticsEvent("call_manager");
    }

    if (peer) {
      peer.oniceconnectionstatechange = () => {}; // prevent this being called during fallback so we don't get an error notification
      peer.close();
    }

    const pusherChannelName = `private-telephone-entry-property-${property.id}`;
    if (this.props.resident) {
      this.socket?.send(
        JSON.stringify({
          event: "callFinished",
          data: { remoteClient: this.props.resident.id },
        })
      );
    }

    window.AudioContext = undefined;
    window.webkitAudioContext = undefined;

    this.telnyxClient = new TelnyxRTC({
      login: call.sip_username,
      password: call.sip_password,
    }).on("telnyx.ready", async () => {
      const params = {
        intercomCallId: call.intercomCallId,
        pusherChannelName,
        to: resident ? resident.directory_phone : property.phone,
        username: `sip:${call.sip_username}@sip.telnyx.com`,
      };

      dispatch(persistLog("SIP : Connected, calling...", params));

      await client.post("telephone-entry/sip-call", { json: params });
    });

    this.telnyxClient
      .on("telnyx.notification", this.handleSIPNotification)
      .on("telnyx.error", (e: Error) => {
        dispatch(persistLog("SIP : Error: ", e.message));
        dispatch(
          pushNotification(
            createNotification(
              "ERROR",
              "An Error Occurred",
              "Please try again",
              null
            )
          )
        );
        this.handleEscape();
      });

    this.telnyxClient.remoteElement = this.remoteVideoRef;
    this.telnyxClient.connect();
  };

  handleClientHangup = (msg: ClientHangupMessage) => {
    const { call } = this.state;
    const { dispatch } = this.props;

    if (call && call.intercomCallId === msg.call_id) {
      this.endCall(msg.open_gate === true);
      if (msg.open_gate === true) {
        firebase.logAnalyticsEvent("video_call_access_granted");
      } else {
        dispatch(
          pushNotification(
            createNotification(
              "ERROR",
              "Sorry, you weren't granted access",
              null,
              "sorryNoAccess"
            )
          )
        );
      }
    }
  };

  handleClientDeny = (msg: ClientDenyMessage) => {
    const { call } = this.state;
    const { dispatch } = this.props;

    if (call && call.intercomCallId === msg.call_id) {
      this.endCall(msg.open_gate === true);
      if (msg.open_gate === true) {
        firebase.logAnalyticsEvent("video_call_denied_access_granted");
      } else {
        firebase.logAnalyticsEvent("video_call_denied");
        dispatch(
          pushNotification(
            createNotification(
              "ERROR",
              "Sorry, you weren't granted access",
              null,
              "sorryNoAccess"
            )
          )
        );
      }
    }
  };

  handleClientCandidate = (msg: ClientCandidateMessage) => {
    const { call, peer } = this.state;
    const { dispatch } = this.props;

    if (msg.candidate && call && call.intercomCallId === msg.call_id) {
      if (peer) {
        peer.addIceCandidate(new RTCIceCandidate(msg.candidate));
      } else {
        dispatch(persistLog("sdp adding candidates"));
        this.setState((state) => ({
          candidates: state.candidates.concat(msg.candidate),
        }));
      }
    }
  };

  handleSIPNotification = (notification: TelnyxNotification) => {
    const { dispatch } = this.props;
    if (notification.type === "callUpdate") {
      this.activeSIPCall = notification.call;

      if (this.activeSIPCall.state === "ringing") {
        this.activeSIPCall.answer();
      } else if (this.activeSIPCall.state === "active") {
        stopRinging();
        this.remoteAudioRef.current.srcObject = this.activeSIPCall.remoteStream;
      } else if (this.activeSIPCall.state === "destroy") {
        firebase.logAnalyticsEvent("sip_call_denied");
        dispatch(
          pushNotification(
            createNotification(
              "ERROR",
              "Sorry, you weren't granted access",
              null,
              "sorryNoAccess"
            )
          )
        );
        this.endCall(false);
      }
    }
  };

  handleSIPMessage = (msg: SIPMessage) => {
    const { dispatch } = this.props;
    const { call } = this.state;

    dispatch(persistLog("SIP : Handling websocket message", msg, call));

    if (call && msg.room === call.intercomCallId) {
      if (msg.connected) {
        dispatch(persistLog("SIP : Remote connected", call));
        stopRinging();
        this.setState({ isConnected: true });
      } else if (msg.open) {
        firebase.logAnalyticsEvent("sip_call_access_granted");
        this.endCall(true);
      }
    }
  };

  handleCallAnswered = async (data: any) => {
    const { peer } = this.state;
    this.socket?.send(
      JSON.stringify({
        event: "readyForCandidates",
        data: {
          remoteClient: this.props.resident.id,
        },
      })
    );
    await peer?.setRemoteDescription(
      new RTCSessionDescription(data.data.rtcMessage)
    );
  };

  handleEndCall = (data: any) => {
    this.socket?.send(
      JSON.stringify({
        event: "callFinished",
        data: {
          remoteClient: this.props.resident.id,
        },
      })
    );

    this.endCall(data.data.open_gate);
  };

  handleNewCall = async () => {
    const { peer } = this.state;

    const offer = await peer?.createOffer();
    await peer?.setLocalDescription(offer);

    this.socket?.send(
      JSON.stringify({
        event: "call",
        data: {
          remoteClient: this.props.resident.id,
          rtcMessage: offer,
        },
      })
    );
  };

  handleIceCandidate = (data: any) => {
    const { peer } = this.state;
    peer?.addIceCandidate(new RTCIceCandidate(data.data.rtcMessage));
  };

  handleButton = (e: NormalizedButtonEvent) => {
    if (e.detail.button === NORMALIZED_BUTTONS.BACK) {
      this.handleEscape();
    }
  };

  handleEscape = () => {
    const { history } = this.props;
    const { call } = this.state;

    stopRinging();

    if (!call) {
      this.cleanup();
      history.replace("/");
    } else {
      this.endCall(false);
    }
  };

  render() {
    const { resident } = this.props;
    const { isConnected } = this.state;

    return (
      <div className="wrapper">
        <h1 className="page-title">
          Call {resident ? resident.directory_display_name : "a Manager"}
        </h1>
        <div className="row">
          <div className="centered-box">
            <video ref={this.localVideo} muted />
            <video className="hide" ref={this.remoteVideoRef} />
            <audio ref={this.remoteAudioRef} autoPlay />
            <h2 className="muted">(Smile, you're on camera!)</h2>
            {isConnected ? (
              <img
                className="phoneCallActive"
                src={PhoneCallActive}
                alt="Connected"
              />
            ) : (
              <div className="phoneCall">
                <CallResident />
              </div>
            )}
            <h2>
              {isConnected ? "Connected to" : "Calling"}{" "}
              {resident ? resident.directory_display_name : "manager"}
              {isConnected ? "" : "..."}
            </h2>
          </div>
        </div>
        <div className="footer-actions">
          <CancelButton onClick={this.handleEscape} />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState, ownProps: ComponentProps) => {
  const params = new URLSearchParams(ownProps.location.search);
  const residentId: string | null = params.get("resident");

  const resident = state.property.residents.find(
    (res: Resident) => res.id === residentId
  );

  const recentCalls =
    resident && residentId
      ? state.app.recentCalls[residentId] || 0
      : state.app.recentCalls.manager;

  return {
    resident,
    recentCalls,
    property: state.property.meta,
    device: state.property.device,
    staff: state.property.staff,
  };
};

export default connect(mapStateToProps)(Call);
